import { FlightRequest, FlightSegment } from '@appTypes/api.types';
import { AirportTreeItem } from '@appTypes/redefined-types';

/**
 * Q-9 (docs#447 point 1, Amiran 29.10.2026 "Show with a mark"; docs features/website/airport-picker/airport-picker.md R-3): an AIRPORT
 * search keeps the rows that fly from / to another airport of the same city, and the row marks that airport "Other <City> airport".
 * A CITY (or country) pick shows the same rows without a mark. The city name comes from the catalogue tree (C15), never from code.
 */

/** The city of one airport in the catalogue tree (country -> city -> airport). */
export interface AirportCity {
  cityUid: string | null;
  cityName: string | null;
}

/** One row end that is not the airport the agent picked; `city` null = the catalogue and the row do not name it. */
export interface OtherAirportMark {
  city: string | null;
}

/** `out-dep` | `out-arr` | `back-dep` | `back-arr` -> the mark of that end of the row (absent = no mark). */
export type OtherAirportMarks = Partial<Record<OtherAirportEnd, OtherAirportMark>>;
export type OtherAirportEnd = 'out-dep' | 'out-arr' | 'back-dep' | 'back-arr';

const code = (value?: string | null) => (value ?? '').trim().toUpperCase();
const isType = (value: string | null | undefined, type: string) => (value ?? '').trim().toLowerCase() === type;

/** IATA code (upper case) -> its city, read from the catalogue tree GetAirportTree answers. */
export function airportCityIndex(tree?: AirportTreeItem[] | null): Map<string, AirportCity> {
  const index = new Map<string, AirportCity>();
  const walk = (nodes: AirportTreeItem[] | null | undefined, city: AirportCity | null) => {
    for (const node of nodes ?? []) {
      if (isType(node.type, 'airport')) {
        if (node.value && !index.has(code(node.value)))
          index.set(code(node.value), city ?? { cityUid: null, cityName: null });
        continue;
      }
      const next = isType(node.type, 'city') ? { cityUid: node.value ?? null, cityName: node.label ?? null } : city;
      walk(node.children, next);
    }
  };
  walk(tree, null);
  return index;
}

/**
 * The mark of one row end. Given the agent picked an AIRPORT (`type` airport) and the row uses another airport code, Then the end is
 * marked with the city of the row's airport (catalogue first, then the picked airport's catalogue city, then the segment's own city).
 * A city / country pick, a missing pick or the picked airport itself gives no mark.
 */
export function otherAirportMark(
  picked: { location?: string | null; type?: string | null } | null | undefined,
  rowCode: string | null | undefined,
  cities: Map<string, AirportCity>,
  segmentCity?: string | null
): OtherAirportMark | null {
  if (!picked || !isType(picked.type, 'airport')) return null;
  const pickedCode = code(picked.location);
  const row = code(rowCode);
  if (!pickedCode || !row || pickedCode === row) return null;
  const city = cities.get(row)?.cityName ?? cities.get(pickedCode)?.cityName ?? segmentCity ?? null;
  const name = city?.trim();
  // an empty name falls back to the generic chip "Other airport of the city"
  return { city: name?.length ? name : null };
}

/**
 * The marks of a whole row. The outbound leg answers the first flight request; the return leg answers the second one when the search
 * has it (a multi-city search), otherwise the first one turned around (a round trip is one request with a return date).
 */
export function otherAirportMarks(
  requests: FlightRequest[] | null | undefined,
  out: FlightSegment[] | null | undefined,
  back: FlightSegment[] | null | undefined,
  cities: Map<string, AirportCity>
): OtherAirportMarks {
  const marks: OtherAirportMarks = {};
  const first = requests?.[0];
  if (!first) return marks;
  const second = requests?.[1];
  const backDep = second
    ? { location: second.departureLocation, type: second.departureType }
    : { location: first.arrivalLocation, type: first.arrivalType };
  const backArr = second
    ? { location: second.arrivalLocation, type: second.arrivalType }
    : { location: first.departureLocation, type: first.departureType };
  const set = (end: OtherAirportEnd, mark: OtherAirportMark | null) => {
    if (mark) marks[end] = mark;
  };
  const outFirst = out?.[0];
  const outLast = out?.at(-1);
  if (outFirst)
    set(
      'out-dep',
      otherAirportMark(
        { location: first.departureLocation, type: first.departureType },
        outFirst.departureAirportCode,
        cities,
        outFirst.departureCityName
      )
    );
  if (outLast)
    set(
      'out-arr',
      otherAirportMark(
        { location: first.arrivalLocation, type: first.arrivalType },
        outLast.arrivalAirportCode,
        cities,
        outLast.arrivalCityName
      )
    );
  const backFirst = back?.[0];
  const backLast = back?.at(-1);
  if (backFirst)
    set('back-dep', otherAirportMark(backDep, backFirst.departureAirportCode, cities, backFirst.departureCityName));
  if (backLast)
    set('back-arr', otherAirportMark(backArr, backLast.arrivalAirportCode, cities, backLast.arrivalCityName));
  return marks;
}
